import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

interface SavingsDistributionChartProps {
  data: Array<{
    category: string;
    estimated_savings: number;
    released_savings: number;
    percentage: number;
  }>;
  loading?: boolean;
}

const SavingsDistributionChart: React.FC<SavingsDistributionChartProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Distribution by Category</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const rows = Array.isArray(data) ? [...data] : [];

  if (rows.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Distribution by Category</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No savings distribution data available
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    if (value >= 1000000000) {
      return `€${(value / 1000000000).toFixed(1)}B`;
    } else if (value >= 1000000) {
      return `€${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `€${(value / 1000).toFixed(1)}K`;
    }
    return `€${value.toFixed(0)}`;
  };

  const CATEGORY_COLORS = {
    'Safety': '#ef4444',      // Red
    'Quality': '#3b82f6',     // Blue
    'Delivery': '#10b981',    // Green
    'Cost': '#f59e0b',        // Yellow
    'Morale': '#8b5cf6'       // Purple
  };

  const totalEstimated = rows.reduce((sum, item) => sum + item.estimated_savings, 0);
  const totalReleased = rows.reduce((sum, item) => sum + item.released_savings, 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Savings Distribution by Category</h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ category, percentage }: any) => `${category}: ${(percentage as number).toFixed(1)}%`}
              outerRadius={80}
              fill="#8884d8"
              dataKey="estimated_savings"
              nameKey="category"
            >
              {rows.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={CATEGORY_COLORS[entry.category as keyof typeof CATEGORY_COLORS] || '#6b7280'} 
                />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: number, name: string, props: any) => [
                formatCurrency(value),
                'Estimated Savings'
              ]}
              labelFormatter={(label) => `Category: ${label}`}
            />
            <Legend 
              verticalAlign="bottom" 
              height={36}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Category Breakdown</h4>
          <div className="space-y-2">
            {[...rows]
              .sort((a, b) => b.estimated_savings - a.estimated_savings)
              .map((item) => (
                <div key={item.category} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center">
                    <div 
                      className="w-3 h-3 rounded mr-2" 
                      style={{ backgroundColor: CATEGORY_COLORS[item.category as keyof typeof CATEGORY_COLORS] || '#6b7280' }}
                    ></div>
                    <span className="text-sm font-medium">{item.category}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{formatCurrency(item.estimated_savings)}</span>
                    <span className="ml-1 text-gray-500">({item.percentage.toFixed(1)}%)</span>
                  </div>
                </div>
              ))}
          </div>
        </div>
        <div>
          <h4 className="font-medium text-gray-900 mb-2">Financial Summary</h4>
          <div className="space-y-3">
            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-blue-600">Total Estimated</span>
                <span className="font-bold text-blue-600">{formatCurrency(totalEstimated)}</span>
              </div>
              <div className="text-xs text-blue-500 mt-1">
                Across all categories
              </div>
            </div>
            <div className="bg-green-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-green-600">Total Released</span>
                <span className="font-bold text-green-600">{formatCurrency(totalReleased)}</span>
              </div>
              <div className="text-xs text-green-500 mt-1">
                Realized savings
              </div>
            </div>
            <div className="bg-purple-50 p-3 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-sm text-purple-600">Top Category</span>
                <span className="font-bold text-purple-600">
                  {data.reduce((max, item) => item.estimated_savings > max.estimated_savings ? item : max, data[0])?.category}
                </span>
              </div>
              <div className="text-xs text-purple-500 mt-1">
                {data.reduce((max, item) => item.estimated_savings > max.estimated_savings ? item : max, data[0])?.percentage.toFixed(1)}% of total
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SavingsDistributionChart;
